// The hooks here are updated at most every component mount,
// and provide a reasonable initial state while waiting for core response
// For direct async calls use getInstalledGames and getRunningGames from app actions

import React, { useEffect, useMemo } from "react";

import { getInstalledGames, getRunningGames } from "@/app/actions.mjs";
import { GAME_SYMBOLS } from "@/app/constants.mjs";
import { subscribe } from "@/util/game-handlers.mjs";

// This will be the same value 99.99% of the time on the same system
const last: Record<PropertyKey, symbol[]> = {
  installedGames: [],
  runningGames: [],
};

export const useInstalledGames: () => symbol[] = () => {
  const [installedGames, setInstalledGames] = React.useState(
    last.installedGames,
  );

  useEffect(() => {
    getInstalledGames().then((games) => {
      // check if last installed games is the same as the current installed games
      const didChange =
        games &&
        (installedGames?.length !== games.length ||
          installedGames.some((a, i) => a !== games[i]));
      if (!didChange) return;

      setInstalledGames(games);
      last.installedGames = games;
    });
  }, []);

  return installedGames;
};

export const useGameInstalledMap: () => Record<symbol, boolean> = () => {
  const installedGames = useInstalledGames();

  return useMemo(
    () =>
      Object.fromEntries(
        GAME_SYMBOLS.map((game) => {
          return [game, !!installedGames?.includes(game)];
        }),
      ) as Record<symbol, boolean>,
    [installedGames],
  );
};

export const useRunningGames = () => {
  const [runningGames, setRunningGames] = React.useState(last.runningGames);

  useEffect(() => {
    getRunningGames().then((games) => {
      // check if last installed games is the same as the current installed games
      const didChange =
        games &&
        (runningGames?.length !== games.length ||
          runningGames.some((a, i) => a !== games[i]));
      if (!didChange) return;

      setRunningGames(games);
      last.runningGames = games;
    });
    const unsubscribe = subscribe((game, newState) => {
      if (newState) {
        setRunningGames((rgs) => {
          const a = Array.isArray(rgs) ? [...rgs] : [];
          if (!a.includes(game)) a.push(game);
          return a;
        });
      } else {
        setRunningGames((rgs) => rgs.filter((g) => g !== game));
      }
    });
    return () => unsubscribe();
  }, []);

  return runningGames;
};

export const useRunningGamesMap: () => Record<symbol, boolean> = () => {
  const runningGames = useRunningGames();

  return useMemo(
    () =>
      Object.fromEntries(
        GAME_SYMBOLS.map((game) => {
          return [game, !!runningGames?.includes(game)];
        }),
      ) as Record<symbol, boolean>,
    [runningGames],
  );
};
